import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { AppLayout } from "@/components/AppLayout";
import {
  Gift, Upload, Users, Check, Mail, Clock, Crown, Star, Gem, Sparkles,
  Loader2, Copy, ArrowRight, ChevronLeft, ChevronRight, Send, Trash2,
  FileSpreadsheet, AlertCircle, X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

const TIER_META = {
  omni_plus: { icon: Star, gradient: "from-amber-500 to-yellow-400", accent: "text-amber-600" },
  duo: { icon: Gem, gradient: "from-cyan-500 to-blue-500", accent: "text-cyan-600" },
  platinum: { icon: Crown, gradient: "from-purple-500 to-pink-500", accent: "text-purple-600" },
  diamond: { icon: Sparkles, gradient: "from-violet-500 to-indigo-500", accent: "text-violet-600" },
};

const MAX_RECIPIENTS = 100;
const EMAIL_RE = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;

export default function BulkGiftPage() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}` };
  const fileInputRef = useRef(null);

  const [step, setStep] = useState(1);
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [emails, setEmails] = useState([]);
  const [emailInput, setEmailInput] = useState("");
  const [selectedTier, setSelectedTier] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState("1_month");
  const [note, setNote] = useState("");
  const [anonymous, setAnonymous] = useState(false);

  const [result, setResult] = useState(null);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const { data } = await axios.get(`${API}/gift/options`, { headers });
        setOptions(data);
        const tierKeys = Object.keys(data.tiers);
        if (tierKeys.length > 0) setSelectedTier(tierKeys[0]);
      } catch {
        toast.error("Failed to load gift options");
      } finally {
        setLoading(false);
      }
    };
    fetchOptions();
  }, []);

  const parseEmails = (text) => {
    const found = text
      .split(/[\n,;\s]+/)
      .map(e => e.trim().toLowerCase())
      .filter(e => e && EMAIL_RE.test(e));
    return [...new Set(found)];
  };

  const handleCSVUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target?.result || "";
      const parsed = parseEmails(text);
      if (parsed.length === 0) {
        toast.error("No valid emails found in file");
        return;
      }
      const merged = [...new Set([...emails, ...parsed])].slice(0, MAX_RECIPIENTS);
      setEmails(merged);
      toast.success(`Added ${parsed.length} email${parsed.length !== 1 ? "s" : ""} from file`);
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const handlePasteEmails = () => {
    const parsed = parseEmails(emailInput);
    if (parsed.length === 0) {
      toast.error("No valid emails found");
      return;
    }
    const merged = [...new Set([...emails, ...parsed])].slice(0, MAX_RECIPIENTS);
    setEmails(merged);
    setEmailInput("");
    toast.success(`Added ${parsed.length} email${parsed.length !== 1 ? "s" : ""}`);
  };

  const removeEmail = (idx) => setEmails(prev => prev.filter((_, i) => i !== idx));
  const clearEmails = () => setEmails([]);

  const userEmail = (user?.email || "").toLowerCase();
  const hasSelfGift = emails.includes(userEmail);
  const invalidEmails = emails.filter(e => !EMAIL_RE.test(e));

  const getPerGiftAmount = () => {
    if (!options || !selectedTier || !selectedDuration) return 0;
    const tier = options.tiers[selectedTier];
    const mults = { "1_month": 1, "3_months": 3, "6_months": 5.5, "12_months": 10 };
    return tier.price_monthly * (mults[selectedDuration] || 1);
  };

  const totalAmount = (getPerGiftAmount() * emails.length).toFixed(2);

  const canProceed = () => {
    if (step === 1) return emails.length > 0 && emails.length <= MAX_RECIPIENTS && !hasSelfGift && invalidEmails.length === 0;
    if (step === 2) return !!selectedTier && !!selectedDuration;
    return true;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/gift/bulk`, {
        tier: selectedTier,
        duration: selectedDuration,
        emails,
        note: note.trim(),
        anonymous,
        origin_url: window.location.origin,
      }, { headers });

      if (data.status === "redirect" && data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        setResult(data);
        setStep(4);
        toast.success(`${data.recipient_count} gifts created!`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create bulk gifts");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="flex-1 flex flex-col items-center justify-start p-4 sm:p-6 overflow-y-auto">
        <div className="w-full max-w-2xl">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center mb-3">
              <Users className="w-7 h-7 text-white" />
            </div>
            <h1 data-testid="bulk-gift-title" className="text-2xl sm:text-3xl font-heading font-bold">Bulk Gift</h1>
            <p className="text-sm text-muted-foreground mt-1">Send Omni subscriptions to your whole team</p>
            <button onClick={() => navigate("/gift")} className="text-xs text-primary hover:underline mt-1">
              Or send a single gift
            </button>
          </div>

          {/* Progress */}
          {step <= 3 && (
            <div data-testid="bulk-steps" className="flex items-center justify-center gap-2 mb-8">
              {["Recipients", "Plan & Duration", "Review"].map((label, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className={cn(
                    "w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold transition-all",
                    step > i + 1 ? "bg-primary text-primary-foreground" :
                    step === i + 1 ? "bg-primary text-primary-foreground ring-2 ring-primary/30" :
                    "bg-muted text-muted-foreground"
                  )}>
                    {step > i + 1 ? <Check className="w-3.5 h-3.5" /> : i + 1}
                  </div>
                  <span className={cn("text-[11px] hidden sm:block", step === i + 1 ? "font-semibold" : "text-muted-foreground")}>{label}</span>
                  {i < 2 && <ChevronRight className="w-3 h-3 text-muted-foreground/50 hidden sm:block" />}
                </div>
              ))}
            </div>
          )}

          {/* Step 1: Recipients */}
          {step === 1 && (
            <div data-testid="bulk-step-recipients" className="space-y-4 animate-in fade-in">
              <p className="text-sm font-medium text-center">Add recipient emails (max {MAX_RECIPIENTS})</p>

              {/* CSV Upload */}
              <div className="flex gap-2">
                <input ref={fileInputRef} type="file" accept=".csv,.txt" onChange={handleCSVUpload} className="hidden" />
                <Button
                  data-testid="bulk-csv-upload-btn"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex-1 rounded-xl gap-2"
                >
                  <FileSpreadsheet className="w-4 h-4" /> Upload CSV
                </Button>
                {emails.length > 0 && (
                  <Button variant="outline" onClick={clearEmails} className="rounded-xl gap-1 text-red-600">
                    <Trash2 className="w-3.5 h-3.5" /> Clear All
                  </Button>
                )}
              </div>

              {/* Manual Paste */}
              <div>
                <label className="text-[11px] font-medium mb-1 block">Paste or type emails (comma, newline, or space separated)</label>
                <textarea
                  data-testid="bulk-email-textarea"
                  value={emailInput}
                  onChange={e => setEmailInput(e.target.value)}
                  rows={4}
                  placeholder="alice@company.com, bob@company.com&#10;carol@company.com"
                  className="w-full px-4 py-3 rounded-2xl border border-border/50 bg-card text-sm focus:outline-none focus:border-primary resize-none font-mono"
                />
                <Button
                  data-testid="bulk-add-emails-btn"
                  onClick={handlePasteEmails}
                  disabled={!emailInput.trim()}
                  variant="outline"
                  className="mt-2 rounded-xl text-xs"
                >
                  <Mail className="w-3.5 h-3.5 mr-1" /> Add Emails
                </Button>
              </div>

              {/* Warnings */}
              {hasSelfGift && (
                <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 dark:bg-red-950/20 px-3 py-2 rounded-xl">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  Your own email is in the list. Remove it to continue.
                </div>
              )}
              {emails.length > MAX_RECIPIENTS && (
                <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 dark:bg-red-950/20 px-3 py-2 rounded-xl">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  Maximum {MAX_RECIPIENTS} recipients per batch.
                </div>
              )}

              {/* Email List */}
              {emails.length > 0 && (
                <div className="bg-card border border-border/50 rounded-2xl p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold">{emails.length} recipient{emails.length !== 1 ? "s" : ""}</span>
                    <span className={cn("text-[10px] font-medium px-2 py-0.5 rounded-full",
                      emails.length <= MAX_RECIPIENTS ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700" : "bg-red-100 text-red-700"
                    )}>
                      {MAX_RECIPIENTS - emails.length} spots left
                    </span>
                  </div>
                  <div className="max-h-48 overflow-y-auto space-y-1">
                    {emails.map((email, i) => (
                      <div key={i} data-testid={`bulk-email-row-${i}`} className="flex items-center justify-between px-2.5 py-1.5 rounded-lg bg-muted/30 hover:bg-muted/50 transition-all group">
                        <span className="text-xs font-mono truncate">{email}</span>
                        <button onClick={() => removeEmail(i)} className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5">
                          <X className="w-3 h-3 text-muted-foreground hover:text-red-500" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Tier & Duration */}
          {step === 2 && options && (
            <div data-testid="bulk-step-plan" className="space-y-5 animate-in fade-in">
              <p className="text-sm font-medium text-center mb-2">Choose a plan for all {emails.length} recipients</p>

              {/* Tier Selection */}
              <div className="space-y-2.5">
                {Object.entries(options.tiers).map(([key, tier]) => {
                  const meta = TIER_META[key] || TIER_META.omni_plus;
                  const Icon = meta.icon;
                  return (
                    <button
                      key={key}
                      data-testid={`bulk-tier-${key}`}
                      onClick={() => setSelectedTier(key)}
                      className={cn(
                        "w-full p-3.5 rounded-2xl border-2 transition-all text-left flex items-center gap-3",
                        selectedTier === key ? "border-primary bg-primary/5" : "border-border/50 hover:border-primary/30 bg-card"
                      )}
                    >
                      <div className={cn("w-9 h-9 rounded-xl bg-gradient-to-br flex items-center justify-center flex-shrink-0", meta.gradient)}>
                        <Icon className="w-4.5 h-4.5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="font-semibold text-sm">{tier.name}</span>
                        <span className="text-xs text-muted-foreground ml-2">${tier.price_monthly}/mo</span>
                      </div>
                      {selectedTier === key && (
                        <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                          <Check className="w-3 h-3 text-primary-foreground" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Duration */}
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground">Duration per recipient</p>
                {options && Object.entries(options.durations).map(([key, dur]) => (
                  <button
                    key={key}
                    data-testid={`bulk-duration-${key}`}
                    onClick={() => setSelectedDuration(key)}
                    className={cn(
                      "w-full p-3 rounded-xl border-2 transition-all flex items-center justify-between",
                      selectedDuration === key ? "border-primary bg-primary/5" : "border-border/50 hover:border-primary/30 bg-card"
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                      <span className="text-sm font-medium">{dur.label}</span>
                    </div>
                    <span className="text-sm font-semibold">${getPerGiftAmount().toFixed(2)}/each</span>
                  </button>
                ))}
              </div>

              {/* Note */}
              <div>
                <label className="text-[11px] font-medium mb-1 block">Note for all recipients (optional)</label>
                <textarea
                  data-testid="bulk-note"
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  maxLength={500}
                  rows={2}
                  placeholder="A message for your team..."
                  className="w-full px-3 py-2.5 rounded-xl border border-border/50 bg-card text-sm resize-none"
                />
              </div>
            </div>
          )}

          {/* Step 3: Review */}
          {step === 3 && options && (
            <div data-testid="bulk-step-review" className="space-y-4 animate-in fade-in">
              <p className="text-sm font-medium text-center mb-2">Review your bulk gift</p>
              <div className="bg-card border border-border/50 rounded-3xl p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Plan</span>
                  <span className="text-sm font-semibold">{options.tiers[selectedTier]?.name}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Duration</span>
                  <span className="text-sm font-semibold">{options.durations[selectedDuration]?.label}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Recipients</span>
                  <span className="text-sm font-semibold">{emails.length} people</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Per gift</span>
                  <span className="text-sm font-semibold">${getPerGiftAmount().toFixed(2)}</span>
                </div>
                {note && (
                  <div>
                    <span className="text-xs text-muted-foreground block mb-1">Note</span>
                    <p className="text-xs bg-muted/30 rounded-xl p-2.5 italic">&ldquo;{note}&rdquo;</p>
                  </div>
                )}
                <div className="border-t border-border/30 pt-3 flex items-center justify-between">
                  <span className="text-sm font-medium">Total</span>
                  <span className="text-2xl font-bold">${totalAmount}</span>
                </div>
              </div>

              {/* Recipient preview */}
              <div className="bg-muted/20 rounded-2xl p-3">
                <p className="text-[11px] font-medium mb-2">{emails.length} Recipients</p>
                <div className="max-h-32 overflow-y-auto space-y-0.5">
                  {emails.map((e, i) => (
                    <p key={i} className="text-[11px] text-muted-foreground font-mono">{e}</p>
                  ))}
                </div>
              </div>

              <p className="text-[10px] text-muted-foreground text-center">
                Each recipient gets a unique gift code. Codes expire 7 days after purchase.
              </p>
            </div>
          )}

          {/* Step 4: Success */}
          {step === 4 && result && (
            <div data-testid="bulk-gift-success" className="text-center space-y-6 animate-in fade-in zoom-in-95">
              <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                <Users className="w-10 h-10 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-heading font-bold mb-1">Bulk Gifts Created!</h2>
                <p className="text-sm text-muted-foreground">
                  {result.recipient_count} x {result.tier_name} — {result.duration_label}
                </p>
                <p className="text-lg font-bold mt-1">Total: ${result.total_amount}</p>
              </div>
              {result.gift_codes && (
                <div className="bg-card border border-border/50 rounded-2xl p-4 text-left">
                  <p className="text-[11px] font-medium mb-2">Gift Codes ({result.gift_codes.length})</p>
                  <div className="max-h-48 overflow-y-auto space-y-1">
                    {result.gift_codes.map((code, i) => (
                      <div key={i} className="flex items-center justify-between px-2.5 py-1.5 rounded-lg bg-muted/30">
                        <code className="text-xs font-mono font-semibold text-primary">{code}</code>
                        <span className="text-[10px] text-muted-foreground">{emails[i]}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex gap-2">
                <Button onClick={() => navigate("/gift")} variant="outline" className="flex-1 rounded-full">Single Gift</Button>
                <Button onClick={() => navigate("/chat")} className="flex-1 rounded-full bg-primary text-primary-foreground gap-1">
                  Chat <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Navigation */}
          {step <= 3 && (
            <div className="flex gap-3 mt-8">
              {step > 1 && (
                <Button variant="outline" onClick={() => setStep(s => s - 1)} className="flex-1 rounded-full">
                  <ChevronLeft className="w-4 h-4 mr-1" /> Back
                </Button>
              )}
              {step < 3 && (
                <Button
                  data-testid="bulk-next-btn"
                  onClick={() => setStep(s => s + 1)}
                  disabled={!canProceed()}
                  className="flex-1 rounded-full bg-primary text-primary-foreground disabled:opacity-40"
                >
                  Next <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}
              {step === 3 && (
                <Button
                  data-testid="bulk-checkout-btn"
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="flex-1 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 text-white"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                  {submitting ? "Processing..." : `Pay $${totalAmount}`}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
